import React from 'react';
import PropTypes from 'prop-types';
import {
  Modal,
  Box,
  Typography,
  IconButton,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import EventBusyIcon from '@mui/icons-material/EventBusy';

const TEAL = '#65C8D0';
const TEAL_DARK = '#3DADB5';
const TEAL_LIGHT = '#E0F6F8';

const LEAVE_TYPE_COLORS = {
  'Sick Leave': { bg: '#FEE2E2', color: '#B91C1C' },
  'Vacation':   { bg: '#DBEAFE', color: '#1D4ED8' },
  'Personal':   { bg: '#EDE9FE', color: '#6D28D9' },
  'Unpaid':     { bg: '#FEF3C7', color: '#B45309' },
  'Other':      { bg: '#F3F4F6', color: '#374151' },
};

const cellSx = {
  fontSize: '0.8rem',
  py: 1,
  px: 1.5,
  borderBottom: `1px solid ${TEAL_LIGHT}`,
  color: '#333',
};

const headCellSx = {
  fontSize: '0.72rem',
  fontWeight: 700,
  py: 1,
  px: 1.5,
  backgroundColor: TEAL_LIGHT,
  color: '#3DADB5',
  textTransform: 'uppercase',
  letterSpacing: 0.5,
  whiteSpace: 'normal',
  lineHeight: 1.3,
};

export default function ViewLeaveModal({ open, onClose, date, records, isAdminOrManager, onEdit, onDelete }) {
  const showActions = isAdminOrManager && (!!onEdit || !!onDelete);
  const theme = useTheme();
  const isMobilePortrait = useMediaQuery(theme.breakpoints.down('sm'));
  const isMobileLandscape = useMediaQuery('(max-height: 500px) and (orientation: landscape)');
  const isMobile = isMobilePortrait || isMobileLandscape;

  const formattedDate = date
    ? new Date(`${date}T00:00:00`).toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
    : '';

  return (
    <Modal open={open} onClose={onClose}>
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: { xs: '95vw', sm: '90vw' },
          maxWidth: 1100,
          bgcolor: 'background.paper',
          borderRadius: 2,
          boxShadow: 24,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          maxHeight: '85vh',
        }}
      >
        {/* Header */}
        <Box
          sx={{
            background: `linear-gradient(135deg, ${TEAL} 0%, ${TEAL_DARK} 100%)`,
            px: 3,
            py: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexShrink: 0,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <EventBusyIcon sx={{ color: '#fff', fontSize: 22 }} />
            <Box>
              <Typography sx={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.68rem', letterSpacing: 1.2, fontWeight: 600 }}>
                EMPLOYEE LEAVES
              </Typography>
              <Typography variant="h6" sx={{ color: '#fff', fontWeight: 700, lineHeight: 1.2 }}>
                Leave Details
              </Typography>
              <Typography sx={{ color: 'rgba(255,255,255,0.85)', fontSize: '0.75rem' }}>
                {formattedDate}
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={onClose} sx={{ color: '#fff' }} size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        {/* Body */}
        <Box sx={{ overflowY: 'auto', flex: 1 }}>
          {records.length === 0 ? (
            <Typography sx={{ textAlign: 'center', color: '#999', fontSize: '0.85rem', py: 5 }}>
              No leave records for this date.
            </Typography>
          ) : (
            <TableContainer sx={{ overflowX: isMobile ? 'auto' : 'hidden' }}>
              <Table
                size="small"
                stickyHeader
                sx={{
                  width: '100%',
                  ...(isMobile ? { minWidth: 700 } : { tableLayout: 'fixed' }),
                }}
              >
                {!isMobile && (
                  <colgroup>
                    <col style={{ width: '12%' }} /> {/* Employee */}
                    <col style={{ width: '9%' }}  /> {/* Job */}
                    <col style={{ width: '9%' }}  /> {/* Task */}
                    <col style={{ width: '11%' }} /> {/* Task Scope */}
                    <col style={{ width: '12%' }} /> {/* Cost Item */}
                    <col style={{ width: '8%' }}  /> {/* Time From */}
                    <col style={{ width: '8%' }}  /> {/* Time To */}
                    <col style={{ width: showActions ? '15%' : '31%' }} /> {/* Description */}
                    {showActions && <col style={{ width: '16%' }} />} {/* Actions */}
                  </colgroup>
                )}
                <TableHead>
                  <TableRow>
                    <TableCell sx={headCellSx}>Employee</TableCell>
                    <TableCell sx={headCellSx}>Job</TableCell>
                    <TableCell sx={headCellSx}>Task</TableCell>
                    <TableCell sx={headCellSx}>Task Scope</TableCell>
                    <TableCell sx={headCellSx}>Cost Item</TableCell>
                    <TableCell sx={headCellSx}>Time From</TableCell>
                    <TableCell sx={headCellSx}>Time To</TableCell>
                    <TableCell sx={headCellSx}>Description</TableCell>
                    {showActions && <TableCell sx={headCellSx}>Actions</TableCell>}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {records.map((record, idx) => {
                    const empName = record.employee_id
                      ? `${record.employee_id.first_name || ''} ${record.employee_id.last_name || ''}`.trim()
                      : '—';
                    const leaveColor = LEAVE_TYPE_COLORS[record.cost_item] || LEAVE_TYPE_COLORS['Other'];
                    return (
                      <TableRow
                        key={record._id}
                        sx={{ backgroundColor: idx % 2 === 0 ? '#fff' : '#f9fefe' }}
                      >
                        <TableCell sx={{ ...cellSx, fontWeight: 600 }}>{empName}</TableCell>
                        <TableCell sx={cellSx}>{record.job_id?.number_str || '—'}</TableCell>
                        <TableCell sx={cellSx}>{record.task_id?.number_str || '—'}</TableCell>
                        <TableCell sx={cellSx}>{record.task_id?.task_scope_id || '—'}</TableCell>
                        <TableCell sx={cellSx}>
                          <Chip
                            label={record.cost_item || '—'}
                            size="small"
                            sx={{
                              backgroundColor: leaveColor.bg,
                              color: leaveColor.color,
                              fontWeight: 700,
                              fontSize: '0.7rem',
                              height: 22,
                              maxWidth: '100%',
                              '& .MuiChip-label': { px: 1, overflow: 'visible' },
                            }}
                          />
                        </TableCell>
                        <TableCell sx={cellSx}>{record.time_from || '—'}</TableCell>
                        <TableCell sx={cellSx}>{record.time_to || '—'}</TableCell>
                        <TableCell sx={{ ...cellSx, whiteSpace: 'pre-wrap', wordBreak: 'break-word', overflow: 'hidden' }}>
                          {record.description || '—'}
                        </TableCell>
                        {showActions && (
                          <TableCell sx={{ ...cellSx, whiteSpace: 'nowrap' }}>
                            <button
                              type="button"
                              onClick={() => onEdit?.(record)}
                              style={{
                                fontSize: '0.7rem',
                                fontWeight: 700,
                                color: '#fff',
                                background: '#F59E0B',
                                border: 'none',
                                borderRadius: '3px',
                                padding: '2px 7px',
                                cursor: 'pointer',
                                marginRight: 4,
                              }}
                            >
                              ✎ Edit
                            </button>
                            <button
                              type="button"
                              onClick={() => onDelete?.(record)}
                              style={{
                                fontSize: '0.7rem',
                                fontWeight: 700,
                                color: '#fff',
                                background: '#EF4444',
                                border: 'none',
                                borderRadius: '3px',
                                padding: '2px 7px',
                                cursor: 'pointer',
                              }}
                            >
                              ✕ Delete
                            </button>
                          </TableCell>
                        )}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      </Box>
    </Modal>
  );
}

ViewLeaveModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  date: PropTypes.string,
  records: PropTypes.array,
  isAdminOrManager: PropTypes.bool.isRequired,
  onEdit: PropTypes.func,
  onDelete: PropTypes.func,
};

ViewLeaveModal.defaultProps = {
  date: '',
  records: [],
};
